import {AfterViewInit, Component, inject, OnInit, ViewChild} from '@angular/core';
import {
  Draggable,
  GridsterComponent,
  GridsterConfig,
  GridsterItem,
  GridsterItemComponentInterface,
  Resizable
} from "angular-gridster2";
import {WidgetOptions} from "../../models/common/WidgetOptions";
import {WidgetEnum} from "../widget/common/WidgetEnum";
import {DashboardService} from "../../services/dashboard/dashboard.service";
import {CreateNewDashboardEvent} from "../../events/CreateNewDashboardEvent";
import {Dashboard} from "../../models/dashboard/Dashboard";
import {ShowSelectedDashboardEvent} from "../../events/ShowSelectedDashboardEvent";
import {ChangeImpersonatedUserEvent} from "../../events/ChangeImpersonatedUserEvent";
import {EditDashboardEvent} from "../../events/EditDashboardEvent";
import {DeleteDashboardEvent} from "../../events/DeleteDashboardEvent";
import {DashboardWidget} from "../../models/dashboard/DashboardWidget";
import {ChangeDashboardEditModeEvent} from "../../events/ChangeDashboardEditModeEvent";
import {ThreeDimModel} from "../../models/widget/threedimmodel/ThreeDimModel";
import {BaseComponentWithDrawer} from "../registry/base-component-with-drawer.component";
import {DashboardFormComponent} from "./dashboard-form/dashboard-form.component";
import {EditModeEvent} from "../../events/EditModeEvent";

interface SafeResizable extends Resizable {
  handles: {
    s: boolean;
    e: boolean;
    n: boolean;
    w: boolean;
    se: boolean;
    ne: boolean;
    sw: boolean;
    nw: boolean;
  };
}

interface Safe extends GridsterConfig {
  draggable: Draggable;
  resizable: SafeResizable;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css', '../common/drawer-style.css',]
})
export class DashboardComponent extends BaseComponentWithDrawer implements OnInit, AfterViewInit {
  get openDashboardFormPopup(): boolean {
    return this._openDashboardFormPopup;
  }

  set openDashboardFormPopup(value: boolean) {
    this._openDashboardFormPopup = value;
    this.eventBus.emitEvent(new EditModeEvent(value))
  }

  @ViewChild('gridsterComponent') gridsterComponent?: GridsterComponent;
  @ViewChild('formDrawerComponent', {static: false}) formDrawerComponent?: DashboardFormComponent;

  itemChange?: (item: GridsterItem, itemComponent: GridsterItemComponentInterface) => void;

  public dashboardService: DashboardService = inject(DashboardService);

  options!: Safe;
  dashboard!: Array<GridsterItem>;

  private _openDashboardFormPopup = false;
  dashboardForForm!: Dashboard;

  loadingData = true;
  protected readonly WidgetEnum = WidgetEnum;

  constructor() {

    super();

    const that = this;

    this.itemChange = function (item: GridsterItem, itemComponent: GridsterItemComponentInterface) {
      // item is resized, save new configuration

      if (!!that.dashboardService?.selectedDashboard?.id && that.dashboardService.dashboardEditMode) {
        if (that.dashboardService.itemHasChanged(item)) {
          that.dashboardService.putDashboardWidget(that.dashboardService.selectedDashboard?.id, DashboardWidget.clone(item)).then(() => {
          });
        }
      }
    };

    this.options = {
      gridType: 'verticalFixed',
      compactType: 'none',
      margin: 15,
      outerMargin: true,
      outerMarginTop: null,
      outerMarginRight: null,
      outerMarginBottom: null,
      outerMarginLeft: null,
      useTransformPositioning: true,
      mobileBreakpoint: 1200,
      minCols: 4,
      maxCols: 10,
      minRows: 3,
      maxRows: 100,
      maxItemCols: 10,
      minItemCols: 1,
      maxItemRows: 100,
      minItemRows: 1,
      maxItemArea: 2500,
      minItemArea: 1,
      defaultItemCols: 1,
      defaultItemRows: 1,
      fixedRowHeight: (window.innerHeight - 123) / 3,
      keepFixedHeightInMobile: true,
      keepFixedWidthInMobile: false,
      scrollSensitivity: 10,
      scrollSpeed: 20,
      enableEmptyCellClick: false,
      enableEmptyCellContextMenu: false,
      enableEmptyCellDrop: true,
      enableEmptyCellDrag: true,
      enableOccupiedCellDrop: false,
      emptyCellDragMaxCols: 50,
      emptyCellDragMaxRows: 50,
      ignoreMarginInRow: false,
      draggable: {
        delayStart: 0,
        enabled: false,
        ignoreContentClass: 'gridster-item-content',
        ignoreContent: false,
        dragHandleClass: 'drag-handler',
        dropOverItems: false
      },
      resizable: {
        enabled: false,
        handles: {
          s: true,
          e: true,
          n: true,
          w: true,
          se: true,
          ne: true,
          sw: true,
          nw: true
        }
      },
      // swap: true,
      pushItems: true,
      disablePushOnDrag: false,
      disablePushOnResize: false,
      pushDirections: {north: true, south: true, east: true, west: true},
      pushResizeItems: false,
      displayGrid: 'onDrag&Resize',
      disableWindowResize: false,
      disableWarnings: true,
      scrollToNewItems: false,
      itemChangeCallback: this.itemChange
    };

  }

  ngOnInit(): void {

    super.ngOnInit();

    this.subscriptions = [
      this.eventBus.onEvent(CreateNewDashboardEvent.NAME, (event: CreateNewDashboardEvent) => {
        this.dashboardForForm = new Dashboard();
        this.dashboardForForm.name = this.translate.instant("DASHBOARD.DEFAULT_NAME");
        this._openDashboardFormPopup = true;
      }),

      this.eventBus.onEvent(ChangeDashboardEditModeEvent.NAME, (event: ChangeDashboardEditModeEvent) => {
        this.dashboardService.dashboardEditMode = event.editMode;
        this.options.scrollToNewItems = event.editMode;
        this.options.displayGrid = event.editMode ? 'always' : 'none';
        this._openDashboardFormPopup &&= event.editMode;
        this.changedOptions();
      }),

      this.eventBus.onEvent(EditDashboardEvent.NAME, (event: EditDashboardEvent) => {
        this._openDashboardFormPopup = false;
        this.cdr.detectChanges();
        let clone = structuredClone(this.dashboardService.selectedDashboard);
        if (!!clone) {
          this.dashboardForForm = clone;
          this._openDashboardFormPopup = true;
        } else {
          this.toastService.openTranslatedErrorToastMessage('ERROR.GENERIC');
        }
      }),

      this.eventBus.onEvent(DeleteDashboardEvent.NAME, async (event: DeleteDashboardEvent) => {
        let result = this.showDeleteDialog(event.value.name).show().done();
        return await result.then(async (dialogResult: any) => {
          if (dialogResult) {
            this.dashboardService.deleteDashboard(event.value.id).then(() => {
              this.getUserDashboards();
            });
          }
        });
      }),

      this.eventBus.onEvent(ShowSelectedDashboardEvent.NAME, (event: ShowSelectedDashboardEvent) => {
        this.getDashboardWidgets();
      }),

      // the impersonated user changed in the topbar: reload the list for the new one, then the
      // usual "pick the default dashboard and show its widgets" flow takes over
      this.eventBus.onEvent(ChangeImpersonatedUserEvent.NAME, (event: ChangeImpersonatedUserEvent) => {
        this.dashboard = new Array<GridsterItem>();
        this.getUserDashboards();
      })
    ];

    window.addEventListener('resize', () => {
      if (!!this.options.api?.optionsChanged) {

      }
    }, false);

  }

  ngAfterViewInit() {
    try {
      this.dashboardService.dashboardEditMode = false;
      this.dashboardService.getAvailableWidgets().catch(err => this.loadingData = false);
      // loaded once per dashboard page open, unlike the dashboards it doesn't change on save/delete.
      // Only administrators get the user selector, so nobody else has a use for the list
      if (this.dashboardService.canShareToOtherUsers())
        this.dashboardService.getUserImpersonationOptions().catch(err => this.loadingData = false);
      setTimeout(() => {
        this.getUserDashboards();
      }, 100);
    } catch (error: any) {
      this.toastService.openErrorToastMessage(error)
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  setWidthOfDrawerPx(): number {
    if (this.appService.isMobileScreen()) {
      return document.documentElement.clientWidth;
    } else if (this.appService.isSmallScreen()) {
      return document.documentElement.clientWidth * 0.6 - 20;
    } else {
      return document.documentElement.clientWidth * 0.3 - 20;
    }
  }


  getUserDashboards() {

    this.dashboardService.getUserDashboards().then((dashboards) => {
      this.cdr.detectChanges();
      if (dashboards.length === 0) {
        // show a full screen div with the message: no dashboard, create one
        this.loadingData = false;
      } else {
        // look for the default dashboard, if none take the first

        const idx = dashboards.findIndex(d => d.defaultDashboard);

        if (idx !== -1) {
          this.dashboardService.selectedDashboard = dashboards[idx];
        } else {
          if (!!this.dashboardService.selectedDashboard) {
            const lastIdx = dashboards.findIndex(d => d.id === this.dashboardService.selectedDashboard!.id);
            if (lastIdx !== -1) {
              this.dashboardService.selectedDashboard = dashboards[lastIdx];
            } else {
              this.dashboardService.selectedDashboard = dashboards[0];
            }
          } else {
            this.dashboardService.selectedDashboard = dashboards[0];
          }
        }

        this.getDashboardWidgets();

      }
      // if no dashboard is present, show a full screen div with the message: no dashboard, create one

    }).catch(err => this.loadingData = false);
  }

  getDashboardWidgets() {

    this.dashboard = new Array<GridsterItem>();
    this.loadingData = true;
    this.cdr.detectChanges();

    this.dashboardService.getWidgetsOfSelectedDashboard().then((widgets) => {
      for (let w of widgets) {
        const maxColsRows = this.dashboardService.getWidgetMaxColsAndRows(w.widgetId);
        const minColsRows = this.dashboardService.getWidgetMinColsAndRows(w.widgetId);
        let newItem: GridsterItem = {
          cols: !!w.colsSize ? w.colsSize : maxColsRows.cols >= 2 ? 2 : 1,
          rows: !!w.rowsSize ? w.rowsSize : maxColsRows.rows >= 2 ? 2 : 1,
          y: w.rowsStart,
          x: w.colsStart,
          id: w.id,
          widgetId: w.widgetId,
          type: w.widgetType,
          name: w.name,
          userConfig: w.userConfig,
          maxItemRows: maxColsRows.rows,
          maxItemCols: maxColsRows.cols,
          minItemRows: minColsRows.rows,
          minItemCols: minColsRows.cols,
          active: true,
          metricInstanceIds: w.metricInstanceIds
        };
        this.dashboard.push(newItem);
      }
      this.dashboard.sort(
        (a, b) => {
          if (a.y - b.y === 0)
            return a.x - b.x;
          else
            return a.y - b.y;
        });
      this.loadingData = false;
      this.cdr.detectChanges();

    }, (error) => {

      this.loadingData = false;

    })

  }

  changedOptions() {
    this.options.draggable.enabled = this.dashboardService.dashboardEditMode;
    this.options.resizable.enabled = this.dashboardService.dashboardEditMode;
    if (this.options.api && this.options.api.optionsChanged) {
      this.options.api.optionsChanged();
    }
  }

  editItemOptions(options: WidgetOptions, item: GridsterItem) {
    const itemIndex = this.dashboard.indexOf(item);
    this.dashboard[itemIndex].options = options;
    this.changedOptions();
  }

  removeItem($event: MouseEvent | TouchEvent, item: GridsterItem) {
    this.showDeleteDialog(item.name).show().done(
      (result: boolean) => {
        if (result) {
          if (item.type === WidgetEnum.THREE_DIM_MODEL) {
            if (!!item.userConfig && !!item.userConfig.models) {
              item.userConfig.models.forEach(
                (modelToDelete: ThreeDimModel) => {
                  caches.open(this.appService.CACHE_NAME)
                    .then(cache => {
                      return cache.delete(modelToDelete.file.objKey);
                    })
                })
            }
          }
          this.dashboardService.deleteWidgetFromDashboard(item.id).then(() => {
            $event.preventDefault();
            $event.stopPropagation();
            this.dashboard.splice(this.dashboard.indexOf(item), 1);
          });
        }
      });
  }

  indexTrackBy(index: number, item: GridsterItem) {
    return item.id;
  };

  selectNewDashboard(dashboard: Dashboard) {
    this._openDashboardFormPopup = false;
    if (!!dashboard) {
      this.loadingData = true;
      this.dashboardService.getUserDashboards().then(async (dashboards) => {
        // move to the dashboard we just created
        let newDashboard = dashboards.find(d => d.id === dashboard.id);
        if (newDashboard) {
          this.dashboardService.selectedDashboard = newDashboard;
          this.getDashboardWidgets()
        }
      }).catch((error) => {
        this.toastService.openErrorToastMessage(error);
        this.loadingData = false;
      });
    }

  }

  dispatchSizeRefreshEvent() {
    window.dispatchEvent(new Event('resize'));
  }

  openCreateDashboard() {
    this.eventBus.emitEvent(new CreateNewDashboardEvent());
  }

  openEditDashboard() {
    this.eventBus.emitEvent(new EditDashboardEvent());
  }
}
